/**
 * Syncs theme context with Supabase theme_preferences when the user is logged in.
 * - On load: fetches preferences from Supabase and hydrates theme context (and localStorage via ThemeProvider).
 * - On change: persists theme state to Supabase (debounced) so settings follow the user across devices.
 * Must be mounted inside both ThemeProvider and AuthProvider.
 */

import { useEffect, useRef } from "react";
import { useTheme } from "@/providers/ThemeProvider";
import { useAuth } from "@/hooks/useAuth";
import {
  useThemePreferencesQuery,
  useThemePreferencesMutation,
} from "@/hooks/useThemePreferences";

const PERSIST_DEBOUNCE_MS = 800;

export function ThemePreferencesSync() {
  const { user } = useAuth();
  const {
    theme,
    colorTheme,
    glowIntensity,
    performanceMode,
    backgroundType,
    backgroundImages,
    setTheme,
    setColorTheme,
    setGlowIntensity,
    setPerformanceMode,
    setBackgroundType,
    setBackgroundImages,
  } = useTheme();

  const { data: serverPrefs, isSuccess } = useThemePreferencesQuery();
  const mutate = useThemePreferencesMutation();
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Skip persistence while hydrating server values into context (prevents loop)
  const isHydratingRef = useRef(false);
  // True while local changes are debounced or the upsert is in flight —
  // a server refetch must not stomp them (last-write-wins).
  const pendingLocalWritesRef = useRef(false);
  // updated_at of the last server row applied to context — dedupes refetches
  const lastAppliedUpdatedAtRef = useRef<string | null>(null);

  // Hydrate theme context from Supabase when we have server data (and when refetched for cross-tab/device sync)
  useEffect(() => {
    if (!user?.id || !isSuccess || !serverPrefs) return;
    // Local changes in flight win; the next refetch returns our own write
    if (pendingLocalWritesRef.current) return;
    // Nothing new from the server — skip re-applying identical values
    if (serverPrefs.updated_at && serverPrefs.updated_at === lastAppliedUpdatedAtRef.current) return;
    lastAppliedUpdatedAtRef.current = serverPrefs.updated_at ?? null;

    isHydratingRef.current = true;
    setTheme(serverPrefs.theme);
    setColorTheme(serverPrefs.color_theme);
    setGlowIntensity(serverPrefs.glow_intensity);
    setPerformanceMode(serverPrefs.performance_mode);
    setBackgroundType(serverPrefs.background_type);
    if (Array.isArray(serverPrefs.background_images)) {
      setBackgroundImages(serverPrefs.background_images);
    }
    // Allow persistence again after React flushes the state updates
    requestAnimationFrame(() => {
      isHydratingRef.current = false;
    });
  }, [user?.id, isSuccess, serverPrefs]);

  // Persist theme to Supabase when any value changes (debounced)
  useEffect(() => {
    if (!user?.id) return;
    // Skip persistence when values are being hydrated from the server
    if (isHydratingRef.current) return;
    pendingLocalWritesRef.current = true;
    if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    persistTimeoutRef.current = setTimeout(() => {
      persistTimeoutRef.current = null;
      mutate.mutate(
        {
          theme,
          color_theme: colorTheme,
          glow_intensity: glowIntensity,
          performance_mode: performanceMode,
          background_type: backgroundType,
          background_images: backgroundImages,
        },
        {
          onSettled: () => {
            pendingLocalWritesRef.current = false;
          },
        },
      );
    }, PERSIST_DEBOUNCE_MS);
    return () => {
      if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    };
  }, [
    user?.id,
    theme,
    colorTheme,
    glowIntensity,
    performanceMode,
    backgroundType,
    backgroundImages,
  ]);

  return null;
}
